import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { BigButton } from "../design/BigButton";
import { Card } from "../design/BigButton";

const LOCK_KEY = "wmleye:pinLockUntil";
const LOCK_MS = 5 * 60 * 1000;
const MAX_ATTEMPTS = 5;

/**
 * Parent gate screen.
 *
 * On mount we ask the backend whether the parent already has a PIN set. If
 * not (fresh registration, or first-time Parent Area access), we switch into
 * "Create PIN" mode with an enter-and-confirm flow. Once created, the parent
 * is sent straight to the dashboard.
 *
 * Existing PIN entry flow is unchanged. Lockout after 5 wrong attempts kicks
 * in on both modes (create-confirm mismatches also count).
 */
export function ParentGateScreen() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("loading"); // 'loading' | 'enter' | 'create-1' | 'create-2'
  const [pin, setPin] = useState("");
  const [firstPin, setFirstPin] = useState(""); // for create-confirm flow
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(() => {
    try {
      const v = localStorage.getItem(LOCK_KEY);
      return v ? Number(v) : 0;
    } catch (_) { return 0; }
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/pin-status");
        if (!alive) return;
        setMode(data.hasPin ? "enter" : "create-1");
      } catch (_) {
        setMode("enter"); // safest default
      }
    })();
    return () => { alive = false; };
  }, []);

  const isLocked = lockedUntil > Date.now();

  function tap(digit) {
    if (isLocked || saving) return;
    setError(null);
    if (pin.length < 6) setPin((p) => p + digit);
  }

  function clearPin() {
    setPin((p) => p.slice(0, -1));
  }

  async function submit() {
    if (isLocked || saving || pin.length < 4) return;
    if (mode === "enter") {
      try {
        await api.post("/auth/verify-pin", { pin });
        navigate("/parent/dashboard", { replace: true });
      } catch (e) {
        const next = attempts + 1;
        setAttempts(next);
        setPin("");
        if (next >= MAX_ATTEMPTS) {
          const until = Date.now() + LOCK_MS;
          setLockedUntil(until);
          try { localStorage.setItem(LOCK_KEY, String(until)); } catch (_) {}
          setError("Too many attempts. Please wait a few minutes and try again.");
        } else {
          setError("That PIN didn't match. Give it another try.");
        }
      }
      return;
    }
    if (mode === "create-1") {
      setFirstPin(pin);
      setPin("");
      setMode("create-2");
      return;
    }
    if (mode === "create-2") {
      if (pin !== firstPin) {
        setError("PINs don't match. Let's try that again.");
        setPin("");
        setFirstPin("");
        setMode("create-1");
        return;
      }
      setSaving(true);
      try {
        await api.post("/auth/set-pin", { pin });
        navigate("/parent/dashboard", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not save PIN. Please try again.");
        setPin("");
        setFirstPin("");
        setMode("create-1");
      } finally {
        setSaving(false);
      }
    }
  }

  const displayLen = mode === "create-2" ? 4 : Math.max(pin.length, 4);
  const heading = mode === "loading" ? "…" : mode === "enter" ? "Parent PIN" : "Set up your Parent PIN";
  const subtitle = mode === "loading"
    ? ""
    : mode === "enter"
    ? "Enter your 4-6 digit PIN to reach the parent area."
    : mode === "create-1"
    ? "This keeps the parent area safe from little hands. Choose a 4-digit PIN."
    : "Type your PIN one more time to confirm.";
  const submitLabel = mode === "enter" ? "Continue"
    : mode === "create-1" ? "Next"
    : (saving ? "Saving…" : "Save PIN");

  return (
    <div className="wmleye-app parent-gate" data-testid="parent-gate">
      <Card className="parent-gate__panel">
        <h1 data-testid="parent-gate-heading">{heading}</h1>
        <p>{subtitle}</p>
        <div className="parent-gate__display" data-testid="parent-gate-display">
          {pin.replace(/./g, "•").padEnd(Math.max(4, displayLen), "◦")}
        </div>
        {error && <div className="parent-gate__error" data-testid="parent-gate-error">{error}</div>}
        {isLocked && (
          <div className="parent-gate__error" data-testid="parent-gate-locked">
            PIN pad is locked. Try again after 5 minutes.
          </div>
        )}
        <div className="parent-gate__pad">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map((d) => (
            <button
              key={d}
              className="parent-gate__key"
              onClick={() => tap(d)}
              disabled={isLocked || saving || mode === "loading"}
              data-testid={`parent-gate-key-${d}`}
              aria-label={`PIN digit ${d}`}
            >
              {d}
            </button>
          ))}
          <button
            className="parent-gate__key parent-gate__key--wide"
            onClick={clearPin}
            disabled={isLocked || saving || mode === "loading"}
            data-testid="parent-gate-clear"
            aria-label="Delete last digit"
          >
            ⌫
          </button>
        </div>
        <div className="parent-gate__actions">
          <BigButton variant="ghost" onClick={() => navigate(-1)} data-testid="parent-gate-back">
            Back
          </BigButton>
          <BigButton
            onClick={submit}
            disabled={isLocked || saving || pin.length < 4 || mode === "loading"}
            data-testid={mode === "enter" ? "parent-gate-submit" : "parent-gate-create-submit"}
          >
            {submitLabel}
          </BigButton>
        </div>
      </Card>
    </div>
  );
}

export function ParentDashboardScreen() {
  const navigate = useNavigate();
  return (
    <div className="wmleye-app parent-dashboard" data-testid="parent-dashboard">
      <Card>
        <h1>Parent Dashboard</h1>
        <p>The full parent dashboard arrives in Phase 3.</p>
        <div className="parent-dashboard__actions">
          <BigButton variant="ghost" onClick={() => navigate(-1)}>← Back</BigButton>
          <BigButton onClick={() => navigate("/profiles")} data-testid="parent-dashboard-profiles">
            Return to profiles
          </BigButton>
        </div>
      </Card>
    </div>
  );
}
